<template>
  <v-container fluid class="pa-0">
    <!-- FT-082: unified command bar (shared with the list) -->
    <bookings-command-bar
      view="board"
      :filters="filters"
      :cars="cars"
      :responsibles="responsibles"
      :cars-loading="carsLoading"
      :responsibles-loading="responsiblesLoading"
      :states="bookingsStates"
      :connected="connected"
      :show-sort="true"
      :xlsx-loading="isLoadingDownloadXlsx"
      :ical-loading="isLoadingDownloadIcalendar"
      @input="onFilters"
      @reset="onReset"
      @load-cars="loadCarsOnce"
      @load-responsibles="loadResponsiblesOnce"
      @refresh="loadBoard()"
      @export-xlsx="toXlsx"
      @export-ical="downloadIcalendar"
    />

    <div class="d-flex px-3" style="gap: 8px; overflow-x: auto">
      <booking-board-column
        v-for="col in columnDefs"
        :key="col.key"
        :title="$t('bookings_board.' + col.key)"
        :bookings="columns[col.key].items"
        :total="columns[col.key].total"
        :loading="columns[col.key].loadingMore"
        :initial-loading="loading && columns[col.key].items.length === 0"
        :states="bookingsStates"
        :bulk-archive="col.key === 'rided' && canBulkArchive"
        :bulk-archiving="bulkArchiving"
        @load-more="loadMore(col.key)"
        @changed="loadBoard()"
        @archived="onCardArchived"
        @bulk-archive="onBulkArchiveRided"
        @send-template="onSendTemplate"
        @open-tasks="onOpenTasks"
      />
    </div>

    <booking-board-template-dialog v-model="templateDialog" :booking="templateBooking" @sent="loadBoard()" />

    <v-dialog v-model="tasksDialog" max-width="680" @input="onTasksDialogToggle">
      <v-card class="pa-2">
        <task-list v-if="tasksBooking" :taskable-type="'Booking'" :taskable-id="tasksBooking.id" />
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import BookingBoardColumn from "@/components/dashboard/bookings/board/BookingBoardColumn.vue";
import BookingsCommandBar from "@/components/dashboard/bookings/BookingsCommandBar.vue";
import BookingBoardTemplateDialog from "@/components/dashboard/bookings/board/BookingBoardTemplateDialog.vue";
import TaskList from "@/components/dashboard/tasks/TaskList.vue";
import { mapBoardColumn } from "@/components/dashboard/bookings/bookingFormatters";
import { reloadPerPage, restoreDepth } from "@/utils/boardReloadDepth";
import bookingsExportMixin from "@/mixins/bookingsExportMixin";
import bookingsQueryState from "@/mixins/bookingsQueryState";
import CableSubscriber from "@/mixins/CableSubscriber";
import { getConsumer } from "@/cable";

const COLUMN_KEYS = ["backlog", "waiting", "in_rent", "rided"];
const PER_PAGE = 20;
// Зеркало BOARD_MAX_PER в bookings_controller#board — чтобы не просить заведомо
// лишнего; фактический потолок берём из per_page, который сервер эхом вернул.
const MAX_PER_PAGE = 200;

export default {
  name: "BookingsBoard",
  components: { BookingBoardColumn, BookingsCommandBar, BookingBoardTemplateDialog, TaskList },
  mixins: [bookingsExportMixin, bookingsQueryState, CableSubscriber],
  data() {
    return {
      loading: false,
      connected: "",
      bookingsSub: null,
      healthTimer: null,
      realtimeTimeout: null,
      bulkArchiving: false,
      templateDialog: false,
      templateBooking: null,
      tasksDialog: false,
      tasksBooking: null,
      columnDefs: COLUMN_KEYS.map((key) => ({ key })),
      columns: COLUMN_KEYS.reduce((acc, key) => {
        acc[key] = { items: [], total: 0, page: 1, loadingMore: false };
        return acc;
      }, {}),
      filters: {
        search: "",
        state: [],
        car_id: null,
        responsible: null,
        date_from: null,
        date_to: null,
        payment_status: null,
        overdue: false,
        deposit_return: false,
        sort_by: "created_at",
        sort_dir: "desc",
      },
      cars: [],
      responsibles: [],
      carsLoading: false,
      responsiblesLoading: false,
      bookingsStates: [
        { text: this.$t("bookings_states.new"), value: "Новая" },
        { text: this.$t("bookings_states.accepted"), value: "Подтверждена" },
        { text: this.$t("bookings_states.not_accepted"), value: "Не подтверждена" },
        { text: this.$t("bookings_states.in_work"), value: "В обработке" },
        { text: this.$t("bookings_states.abandoned"), value: "Недозвон" },
        { text: this.$t("bookings_states.decline"), value: "Отказ клиента" },
        { text: this.$t("bookings_states.no_cars"), value: "Нет машин" },
        { text: this.$t("bookings_states.wait_answer"), value: "Ожидает ответа клиента" },
        { text: this.$t("bookings_states.wait_pay"), value: "Ожидает оплаты" },
        { text: this.$t("bookings_states.active"), value: "Активная" },
        { text: this.$t("bookings_states.cancel"), value: "Отмена" },
        { text: this.$t("bookings_states.rided"), value: "Отъездила" },
        { text: this.$t("bookings_states.return_deposit"), value: "Вернуть залог" },
      ],
    };
  },
  created() {
    // Не-реактивный трекер оптимистично обработанных id (архивация): их cable-эхо
    // из BookingsChannel игнорируем, чтобы не гонять полный рефетч доски.
    this.locallyHandledIds = new Set();
    this.readFiltersFromQuery();
  },
  mounted() {
    this.loadBoard();
    this.subscribeBookings();
  },
  beforeDestroy() {
    this.$cableUnsubscribe(this.bookingsSub);
    this.bookingsSub = null;
    if (this.healthTimer) clearInterval(this.healthTimer);
    if (this.realtimeTimeout) clearTimeout(this.realtimeTimeout);
  },
  computed: {
    totalBookings() {
      return Object.values(this.columns).reduce((sum, col) => sum + (col.total || 0), 0);
    },
    // FT-091: массовую архивацию «Отъездила» показываем только управляющим ролям
    // (паритет с backend-гейтом archive_rided и canAct карточек).
    canBulkArchive() {
      // Тот же смысл, что раньше давал список исключений: сотрудник компании.
      return this.isStaff;
    },
  },
  methods: {
    // FT-091: массовая архивация всех «отъездивших» одной кнопкой. Сервер архивирует
    // через update_all (без per-record broadcast → нет N cable-эхо/loadBoard) и уважает
    // активные фильтры — поэтому шлём те же filterParams, что и доска (набор = числу в
    // диалоге). Broadcast не приходит → обновляем доску сами одним loadBoard. Роль-гейт
    // дублирует серверный (agent/partner/guest — 403).
    async onBulkArchiveRided() {
      const total = this.columns.rided.total;
      if (!total || this.bulkArchiving) return;
      const ok = await this.$confirm({
        title: this.$t("board.archive_all_rided"),
        text: this.$t("board.archive_all_rided_confirm", { count: total }),
        confirmButtonText: this.$t("board.archive_all_rided"),
      });
      if (!ok) return;
      this.bulkArchiving = true;
      try {
        const { data } = await this.axios.post(
          "/api/v1/bookings/archive_rided",
          this.filterParams(),
          { headers: { Authorization: this.$store.getters.getAuthToken } }
        );
        this.$notify(this.$t("board.archive_all_rided_done", { count: data.archived }), "", "success", 5000);
        this.loadBoard();
      } catch (error) {
        this.setError && this.setError(error, this.$t("errors.bad_archivate"));
      } finally {
        this.bulkArchiving = false;
      }
    },
    // FT-082: live board via BookingsChannel (same channel the list uses). Any booking
    // change refreshes the columns; a 30s health-check keeps the live indicator honest.
    subscribeBookings() {
      const company = this.$store.getters.getCompany && this.$store.getters.getCompany.company;
      // Паритет со списком броней (BookingsListView): агент видит доску из
      // своего меню, эндпойнт и так отдаёт только доступные ему брони.
      if ((this.isStaff || this.isAgent) && company && company.id) {
        this.bookingsSub = this.$cableSubscribe(
          { channel: "BookingsChannel", room: company.id },
          {
            connected: () => { this.connected = "success"; },
            disconnected: () => { this.connected = "error"; },
            received: (data) => this.onBookingsReceived(data),
          }
        );
      }
      this.healthTimer = setInterval(() => {
        const consumer = getConsumer();
        this.connected = !!(consumer && consumer.connection && consumer.connection.isOpen());
      }, 30000);
    },
    onBookingsReceived(data) {
      // Эхо собственной оптимистично-применённой архивации — карточка уже убрана
      // локально (onCardArchived), полный рефетч не нужен: гасим его здесь, иначе
      // массовая архивация даст лавину POST /bookings/board через ~3.5с (backend
      // BroadcastBookingJob wait:3s + этот debounce).
      const id =
        data && data.booking != null
          ? data.booking.id != null
            ? data.booking.id
            : data.booking
          : null;
      if (id != null && this.locallyHandledIds.has(id)) {
        this.locallyHandledIds.delete(id);
        return;
      }
      // Чужое изменение — columns rebuilt server-side; debounce so a burst costs one reload
      if (this.realtimeTimeout) clearTimeout(this.realtimeTimeout);
      this.realtimeTimeout = setTimeout(() => this.loadBoard(), 500);
    },
    onCardArchived(id) {
      // Оптимистично убираем заархивированную карточку без полного рефетча доски
      // (архив исключён из всех колонок). Помечаем id, чтобы позднее пришедшее
      // cable-эхо этой же мутации не вызвало лишнюю перезагрузку (onBookingsReceived).
      for (const key of COLUMN_KEYS) {
        const col = this.columns[key];
        const idx = col.items.findIndex((b) => b.id === id);
        if (idx !== -1) {
          col.items.splice(idx, 1);
          col.total = Math.max(0, col.total - 1);
          break;
        }
      }
      this.locallyHandledIds.add(id);
    },
    filterParams() {
      const f = this.filters;
      const p = { sort_by: f.sort_by, sort_dir: f.sort_dir };
      if (f.search) p.search = f.search;
      if (f.state && f.state.length) p.state = f.state;
      if (f.car_id) p.car_id = f.car_id;
      if (f.responsible) p.responsible = f.responsible;
      if (f.date_from) p.date_from = f.date_from;
      if (f.date_to) p.date_to = f.date_to;
      if (f.payment_status) p.payment_status = f.payment_status;
      if (f.overdue) p.overdue = true;
      if (f.deposit_return) p.deposit_return = true;
      return p;
    },
    // Перезагрузка всех колонок. По умолчанию сохраняет открытую ленивой подгрузкой
    // глубину — так её дёргают cable-эхо чужих изменений, ручной refresh, @changed
    // карточек и закрытие диалога задач, и пользователь не должен слетать с 4-й
    // страницы на первую. resetDepth — для смены фильтров/сортировки: это новая
    // выборка, начинаем с первой страницы. Арифметика — utils/boardReloadDepth.
    async loadBoard({ resetDepth = false } = {}) {
      const perPage = resetDepth
        ? PER_PAGE
        : reloadPerPage(COLUMN_KEYS.map((key) => this.columns[key].items.length), PER_PAGE, MAX_PER_PAGE);
      this.loading = true;
      try {
        const { data } = await this.axios.post(
          "/api/v1/bookings/board",
          { per_page: perPage, ...this.filterParams() },
          { headers: { Authorization: this.$store.getters.getAuthToken } }
        );
        COLUMN_KEYS.forEach((key) => {
          const col = data.columns[key];
          // page — по per_page из ответа (сервер мог применить свой потолок), чтобы
          // следующий loadMore(page + 1, PER_PAGE) продолжил ровно с items.length
          const depth = restoreDepth(mapBoardColumn(col.bookings, col.tasks), col.per_page, PER_PAGE);
          this.columns[key].items = depth.items;
          this.columns[key].total = col.total;
          this.columns[key].page = depth.page;
        });
      } catch (error) {
        this.setError && this.setError(error, this.$t("errors.bookings_load"));
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    async loadMore(key) {
      const col = this.columns[key];
      // Load-bearing dedup guard: the column's IntersectionObserver may emit
      // load-more again before its `loading` prop flushes, so this synchronous
      // check on the shared reactive object is the real protection — keep it.
      if (col.loadingMore || col.items.length >= col.total) return;
      col.loadingMore = true;
      try {
        const nextPage = col.page + 1;
        const { data } = await this.axios.post(
          "/api/v1/bookings/board",
          { column: key, page: nextPage, per_page: PER_PAGE, ...this.filterParams() },
          { headers: { Authorization: this.$store.getters.getAuthToken } }
        );
        const more = mapBoardColumn(data.columns[key].bookings, data.columns[key].tasks);
        col.items = col.items.concat(more);
        col.total = data.columns[key].total;
        col.page = nextPage;
      } catch (error) {
        console.log(error);
      } finally {
        col.loadingMore = false;
      }
    },
    onFilters(f) {
      this.filters = f;
      this.writeFiltersToQuery();
      this.loadBoard({ resetDepth: true });
    },
    onReset() {
      this.filters = {
        search: "",
        state: [],
        car_id: null,
        responsible: null,
        date_from: null,
        date_to: null,
        payment_status: null,
        overdue: false,
        deposit_return: false,
        sort_by: "created_at",
        sort_dir: "desc",
      };
      this.writeFiltersToQuery();
      this.loadBoard({ resetDepth: true });
    },
    loadCarsOnce() {
      if (this._carsLoaded) return;
      this._carsLoaded = true;
      this.loadCars();
    },
    loadResponsiblesOnce() {
      if (this._respLoaded) return;
      this._respLoaded = true;
      this.loadResponsibles();
    },
    loadCars() {
      this.carsLoading = true;
      this.axios
        .get("/api/v1/active_cars_for_new_booking", {
          headers: { Authorization: this.$store.getters.getAuthToken },
        })
        .then((response) => {
          this.cars = (response.data.cars.data || []).map((entry) => entry.attributes);
        })
        .catch((error) => console.log(error))
        .finally(() => { this.carsLoading = false; });
    },
    loadResponsibles() {
      this.responsiblesLoading = true;
      this.axios
        .get("/api/v1/get_workers_names_with_ids", {
          headers: { Authorization: this.$store.getters.getAuthToken },
        })
        .then((response) => {
          this.responsibles = response.data;
        })
        .catch((error) => console.log(error))
        .finally(() => { this.responsiblesLoading = false; });
    },
    onSendTemplate(booking) {
      this.templateBooking = booking;
      this.templateDialog = true;
    },
    onOpenTasks(booking) {
      this.tasksBooking = booking;
      this.tasksDialog = true;
    },
    onTasksDialogToggle(open) {
      // сброс через v-if пересоздаёт TaskList — иначе диалог показывает задачи прошлой брони
      if (!open) {
        this.tasksBooking = null;
        this.loadBoard();
      }
    },
  },
};
</script>

<style scoped>
.board-header__title {
  font-size: 1.15rem;
  font-weight: 600;
  letter-spacing: 0.01em;
}
.board-header__total {
  font-size: 0.8rem;
  font-weight: 600;
  opacity: 0.5;
}
/* Segmented view switch: quiet until active */
.board-header__switch >>> .v-btn {
  text-transform: none;
  letter-spacing: 0;
}
</style>
